import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { KeyRound, Plus } from "lucide-react";
import { ApiError } from "@/lib/api";
import { confirmAsync } from "@/components/Toaster";
import {
  type ApiCredentialSummary,
  fetchPresetTemplate,
  useCredentials,
  useCreateCredential,
  useUpdateCredential,
  useDeleteCredential,
  usePublicPresets,
} from "@/lib/credentials";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge, StatusBadge } from "@/components/ui/badge";
import { Alert } from "@/components/ui/alert";
import { EmptyState } from "@/components/ui/empty-state";
import { HelpSection } from "@/components/HelpSection";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";

export function CredentialsSection() {
  const { t } = useTranslation();
  const credentials = useCredentials();
  const presets = usePublicPresets();
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<ApiCredentialSummary | null>(null);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-[12rem] flex-1">
          <p className="text-sm text-fg-muted">{t("credentials.subtitle")}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} iconLeft={<Plus size={16} />}>
          {t("credentials.add")}
        </Button>
      </div>

      <Alert tone="warning" title={t("credentials.warning_title")}>
        {t("credentials.warning_body")}
      </Alert>

      {presets.data && presets.data.length > 0 && (
        <div className="rounded-lg border border-border bg-bg-secondary p-4 space-y-2">
          <div>
            <p className="text-sm font-medium">{t("credentials.presets_title")}</p>
            <p className="text-xs text-fg-muted">{t("credentials.presets_subtitle")}</p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            {presets.data.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded border border-border bg-bg-primary/40 px-3 py-2"
              >
                <span className="font-medium text-fg-primary">{p.label}</span>
                <span className="font-mono text-fg-muted">api_id {p.api_id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {credentials.isLoading && (
        <p className="text-fg-muted text-sm">{t("credentials.loading")}</p>
      )}

      {credentials.data && credentials.data.length === 0 && (
        <EmptyState
          icon={KeyRound}
          title={t("credentials.empty_title")}
          description={t("credentials.empty_hint")}
          action={
            <Button onClick={() => setShowCreate(true)}>{t("credentials.add_first")}</Button>
          }
        />
      )}

      {credentials.data && credentials.data.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full whitespace-nowrap text-sm">
            <thead className="bg-bg-secondary text-left text-fg-muted border-b border-border">
              <tr>
                <th className="px-4 py-3 font-medium">{t("credentials.col.label")}</th>
                <th className="px-4 py-3 font-medium">{t("credentials.col.api_id")}</th>
                <th className="px-4 py-3 font-medium">{t("credentials.col.accounts")}</th>
                <th className="px-4 py-3 font-medium">{t("credentials.col.status")}</th>
                <th className="px-4 py-3 font-medium text-right">
                  {t("credentials.col.actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {credentials.data.map((c) => (
                <CredentialRow
                  key={c.id}
                  credential={c}
                  onEdit={() => setEditTarget(c)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <HelpSection
        i18nKey="credentials.help"
        paragraphs={4}
        list={{
          titleKey: "credentials.help.list_title",
          itemsKey: "credentials.help.list_items",
          itemCount: 3,
        }}
      />

      {showCreate && <CreateCredentialModal onClose={() => setShowCreate(false)} />}
      {editTarget && (
        <EditCredentialDialog
          credential={editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- CredentialRow */

function CredentialRow({
  credential,
  onEdit,
}: {
  credential: ApiCredentialSummary;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const del = useDeleteCredential();

  const statusValue = credential.is_burned ? "burned" : "healthy";

  return (
    <tr className="border-t border-border hover:bg-bg-secondary/50">
      <td className="px-4 py-3">
        <p className="font-medium">{credential.label}</p>
        {credential.notes && (
          <p className="text-xs text-fg-muted mt-0.5 italic">{credential.notes}</p>
        )}
        {credential.is_burned && credential.burn_reason && (
          <p className="text-xs text-danger mt-0.5">
            {t("credentials.burn_reason")}: {credential.burn_reason}
          </p>
        )}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-fg-muted">
        {credential.api_id_masked}
      </td>
      <td className="px-4 py-3">
        <span>{credential.accounts_count}</span>
        {credential.banned_count > 0 && (
          <Badge tone="danger" className="ml-2">
            {credential.banned_count} {t("credentials.banned")}
          </Badge>
        )}
      </td>
      <td className="px-4 py-3">
        <StatusBadge
          domain="credential"
          value={statusValue}
          label={t(`credentials.status.${statusValue}`)}
          dot
        />
      </td>
      <td className="px-4 py-3 text-right space-x-2">
        <Button size="sm" variant="ghost" onClick={onEdit}>
          {t("common.edit")}
        </Button>
        <Button
          size="sm"
          variant="danger"
          disabled={credential.accounts_count > 0 || del.isPending}
          onClick={async () => {
            if (credential.accounts_count > 0) return;
            const yes = await confirmAsync({
              title: t("credentials.delete_confirm", { label: credential.label }),
              confirmKind: "danger",
              confirmLabel: t("credentials.delete"),
            });
            if (yes) void del.mutate(credential.id);
          }}
          title={
            credential.accounts_count > 0
              ? t("credentials.cannot_delete_busy")
              : undefined
          }
        >
          {t("credentials.delete")}
        </Button>
      </td>
    </tr>
  );
}

/* --------------------------------------------------------- EditCredentialDialog */

function EditCredentialDialog({
  credential,
  onClose,
}: {
  credential: ApiCredentialSummary;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateCredential();
  const [label, setLabel] = useState(credential.label);
  const [notes, setNotes] = useState(credential.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        id: credential.id,
        label: label.trim(),
        // Sent as typed: an emptied field must clear the stored note.
        notes: notes.trim(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("credentials.save_error"));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t("credentials.edit_title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_cred_label">{t("credentials.field.label")}</Label>
              <Input
                id="edit_cred_label"
                required
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_cred_notes">{t("credentials.field.notes")}</Label>
              <Input
                id="edit_cred_notes"
                placeholder={t("credentials.field.notes_placeholder")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
            {credential.accounts_count > 0 && (
              <Alert tone="info">
                {t("credentials.reassign_hint", { count: credential.accounts_count })}
              </Alert>
            )}
            {error && <p className="text-sm text-danger">{error}</p>}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={update.isPending}>
              {t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------- CreateCredentialModal */

function CreateCredentialModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateCredential();
  const presets = usePublicPresets();
  const [label, setLabel] = useState("");
  const [apiId, setApiId] = useState("");
  const [apiHash, setApiHash] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);

  const applyTemplate = async (presetId: string) => {
    if (!presetId) return;
    setError(null);
    setTemplateLoading(true);
    try {
      const tpl = await fetchPresetTemplate(presetId);
      setLabel(tpl.label);
      setApiId(String(tpl.api_id));
      setApiHash(tpl.api_hash);
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError(t("credentials.template_error"));
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        label,
        api_id: parseInt(apiId, 10),
        api_hash: apiHash.trim(),
        notes: notes || null,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError(t("credentials.create_error"));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t("credentials.create_title")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-4">
            <p className="text-xs text-fg-muted">
              {t("credentials.create_subtitle")}{" "}
              <a
                href="https://my.telegram.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline"
              >
                my.telegram.org
              </a>
            </p>

            {presets.data && presets.data.length > 0 && (
              <div className="space-y-2 rounded-lg border border-accent/30 bg-accent/5 p-3">
                <Label htmlFor="cred_template">{t("credentials.field.template")}</Label>
                <select
                  id="cred_template"
                  defaultValue=""
                  onChange={(e) => {
                    void applyTemplate(e.target.value);
                    e.target.value = "";
                  }}
                  disabled={templateLoading}
                  className="w-full rounded-lg border border-border bg-bg-secondary px-3 py-2 text-sm"
                >
                  <option value="">
                    {templateLoading
                      ? t("credentials.field.template_loading")
                      : t("credentials.field.template_pick")}
                  </option>
                  {presets.data.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label} (api_id {p.api_id})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-fg-muted">{t("credentials.field.template_hint")}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="cred_label">{t("credentials.field.label")}</Label>
              <Input
                id="cred_label"
                required
                placeholder={t("credentials.field.label_placeholder")}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cred_api_id">api_id</Label>
              <Input
                id="cred_api_id"
                required
                inputMode="numeric"
                pattern="[0-9]+"
                placeholder="12345678"
                value={apiId}
                onChange={(e) => setApiId(e.target.value.replace(/\D/g, ""))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cred_api_hash">api_hash</Label>
              <Input
                id="cred_api_hash"
                required
                minLength={32}
                maxLength={64}
                placeholder="abcdef0123456789..."
                value={apiHash}
                onChange={(e) => setApiHash(e.target.value)}
              />
              <p className="text-xs text-fg-muted">{t("credentials.field.api_hash_hint")}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cred_notes">{t("credentials.field.notes")}</Label>
              <Input
                id="cred_notes"
                placeholder={t("credentials.field.notes_placeholder")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {error && <p className="text-sm text-danger">{error}</p>}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {create.isPending ? t("credentials.creating") : t("credentials.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
